import type { PaletteMode } from "@mui/material";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";

const STORAGE_KEY = "theme-mode";

function getSystemPreference(): PaletteMode {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getStoredMode(): PaletteMode | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "light" || stored === "dark" ? stored : null;
}

function resolveMode(): PaletteMode {
  return getStoredMode() ?? getSystemPreference();
}

function applyMode(mode: PaletteMode) {
  document.documentElement.dataset.theme = mode;
}

// Shared external store so all consumers stay in sync (including across tabs)
let currentMode: PaletteMode = resolveMode();
const listeners = new Set<() => void>();

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot(): PaletteMode {
  return currentMode;
}

function setMode(mode: PaletteMode) {
  currentMode = mode;
  localStorage.setItem(STORAGE_KEY, mode);
  applyMode(mode);
  listeners.forEach((cb) => cb());
}

// Apply on module load so there's no flash of wrong theme
applyMode(currentMode);

export function useThemeMode() {
  const mode = useSyncExternalStore(subscribe, getSnapshot);

  const toggleMode = useCallback(() => {
    setMode(mode === "light" ? "dark" : "light");
  }, [mode]);

  // Sync across tabs via storage event
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === "light" || e.newValue === "dark")) {
        currentMode = e.newValue;
        applyMode(currentMode);
        listeners.forEach((cb) => cb());
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);
}
